import React, { ReactNode } from 'react';
import { Auth0Provider as Auth0ProviderBase } from '@auth0/auth0-react';

interface Auth0ProviderProps {
  children: ReactNode;
}

/**
 * Validates Auth0 domain format
 * @param domain - The Auth0 domain to validate
 * @returns true if domain format is valid, false otherwise
 */
const isValidAuth0Domain = (domain: string): boolean => {
  const validPatterns = [
    /\.auth0\.com$/,
    /\.us\.auth0\.com$/,
    /\.eu\.auth0\.com$/,
    /\.au\.auth0\.com$/,
  ];
  return validPatterns.some(pattern => pattern.test(domain));
};

/**
 * Auth0Provider wrapper component
 * 
 * Configures Auth0 authentication for the application with:
 * - Domain and client ID from environment variables
 * - Refresh tokens enabled for better UX
 * - Local storage for token caching
 * - Account linking support for email matching
 * - Comprehensive validation and error handling
 */
export const Auth0Provider: React.FC<Auth0ProviderProps> = ({ children }) => {
  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;
  const redirectUri = import.meta.env.VITE_AUTH0_REDIRECT_URI || `${window.location.origin}/callback`;

  // Validate required environment variables
  if (!domain || !clientId) {
    const missingVars: string[] = [];
    if (!domain) missingVars.push('VITE_AUTH0_DOMAIN');
    if (!clientId) missingVars.push('VITE_AUTH0_CLIENT_ID');

    console.error(
      '❌ Missing Auth0 configuration. Please set the following environment variables in your .env file:',
      missingVars.join(', ')
    );
    console.error(
      '📋 Setup instructions:',
      '\n1. Copy .env.example to .env',
      '\n2. Update VITE_AUTH0_DOMAIN with your Auth0 tenant domain (e.g., your-tenant.auth0.com)',
      '\n3. Update VITE_AUTH0_CLIENT_ID with your Auth0 application client ID',
      '\n4. Ensure your Auth0 application callback URL matches:', redirectUri
    );

    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="text-center p-8 max-w-md">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">
            Configuration Error
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            Missing Auth0 configuration. Please set the following environment variables in your <code className="bg-slate-200 dark:bg-slate-800 px-2 py-1 rounded">.env</code> file:
          </p>
          <ul className="text-left text-sm text-slate-500 dark:text-slate-400 space-y-2 mt-4 mb-6 bg-slate-100 dark:bg-slate-800 p-4 rounded-lg">
            {missingVars.map((varName) => (
              <li key={varName} className="font-mono">• {varName}</li>
            ))}
          </ul>
          <div className="text-left text-xs text-slate-500 dark:text-slate-400 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <p className="font-semibold mb-2">Quick Setup:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Copy <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">.env.example</code> to <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">.env</code></li>
              <li>Get your Auth0 credentials from <a href="https://manage.auth0.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 underline">Auth0 Dashboard</a></li>
              <li>Update the environment variables with your Auth0 values</li>
              <li>Restart the development server</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  // Validate domain format
  if (!isValidAuth0Domain(domain)) {
    console.warn(
      '⚠️ Auth0 domain format might be incorrect. Expected format: your-tenant.auth0.com (or .us.auth0.com, .eu.auth0.com, .au.auth0.com)',
      '\nCurrent domain:', domain
    );
  }

  // Validate client ID format (basic check - should be a string)
  if (typeof clientId !== 'string' || clientId.length < 10) {
    console.warn(
      '⚠️ Auth0 Client ID format might be incorrect. Client IDs are typically longer strings.',
      '\nCurrent client ID length:', clientId.length
    );
  }

  return (
    <Auth0ProviderBase
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: redirectUri,
        audience: audience || undefined,
        // Enable account linking by email
        connection: undefined, // Let user choose or use default
      }}
      useRefreshTokens={true}
      cacheLocation="localstorage"
      // Handle redirect callback - appState is passed from loginWithRedirect
      onRedirectCallback={(appState) => {
        // The appState.returnTo will be handled by AuthCallback component
        // This callback is called after Auth0 processes the redirect
        // We just need to ensure the URL is clean
        if (appState?.returnTo) {
          // Store the returnTo in sessionStorage so AuthCallback can access it
          sessionStorage.setItem('auth0_returnTo', appState.returnTo);
        }
      }}
    >
      {children}
    </Auth0ProviderBase>
  );
};
